import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../components/AuthView';
import { useMaestroStore } from '../store/maestroStore';
import { streamLibraryService } from '../services/streamLibraryService';
import { fetchCloudAuthenticated } from '../services/api';
import { detectPlatform } from '../utils/platform';
import { saveRecentAlbumFromTrack } from '../store/recentAlbumsStore';
import {
  isStorableStreamLibraryAlbum,
  isStorableStreamLibraryArtist,
} from '../utils/streamLibraryProvenance';

const FLUSH_INTERVAL_MS = 15_000;
const MAX_QUEUE_SIZE = 50;

let eventQueue = [];
let sessionId = null;
const _streamLibrarySent = new Set();
/** Dedupe transport re-drives / hook remounts for the same queued track. */
let _lastTrackPlayedDedupeKey = null;

function trackPlayedDedupeKey(track, queueIndex) {
  const id = track?.qobuzTrackId || track?.id || track?.file_path;
  if (!id) return null;
  const idx = Number.isFinite(queueIndex) ? queueIndex : -1;
  return `${String(id)}@${idx}`;
}

function getSessionId() {
  if (!sessionId) sessionId = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  return sessionId;
}

function getDeviceId() {
  return detectPlatform().isIOSNativeApp ? 'ios' : 'desktop';
}

async function flushEvents() {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0, MAX_QUEUE_SIZE);
  try {
    const res = await fetchCloudAuthenticated('/api/valet/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
    });
    if (!res.ok) {
      eventQueue.unshift(...batch);
    }
  } catch {
    eventQueue.unshift(...batch);
  }
}

/**
 * Queue a personalization event for batched delivery.
 * Safe to call from anywhere (components, handlers, etc.)
 */
export function trackEvent(eventType, { entityType, entityId, entityName, metadata } = {}) {
  eventQueue.push({
    eventType,
    entityType: entityType || null,
    entityId: entityId || null,
    entityName: entityName || null,
    metadata: metadata || {},
    sessionId: getSessionId(),
    deviceId: getDeviceId(),
    timestamp: Date.now(),
  });

  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    flushEvents();
  }
}

/**
 * Hook that starts the personalization event pipeline.
 * Mount once in AppLayout — it handles flush intervals, track change detection,
 * and page visibility (flush on background/close).
 */
export default function usePersonalization() {
  const auth = useAuth();
  const prevTrackRef = useRef(null);
  const trackStartRef = useRef(null);

  useEffect(() => {
    if (!auth?.accessToken) return;

    const interval = setInterval(() => flushEvents(), FLUSH_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flushEvents();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const handleBeforeUnload = () => flushEvents();
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushEvents();
    };
  }, [auth?.accessToken]);

  // Track change detection: emit track_played on new track, track_completed on track switch.
  // Compares by track ID (not object reference) to avoid duplicate events when
  // setCurrentTrack is called multiple times for the same track (e.g. once for
  // UI, once with stream URL). Skips events for tracks with _fromHistory flag.
  useEffect(() => {
    const prevIdRef = { current: null };
    const unsub = useMaestroStore.subscribe((state) => {
      const currentTrack = state.currentTrack;
      const queueIndex = state.queueIndex;
      const prev = prevTrackRef.current;
      const curId = currentTrack?.qobuzTrackId || currentTrack?.id;

      if (!currentTrack) {
        _lastTrackPlayedDedupeKey = null;
        prevTrackRef.current = null;
        prevIdRef.current = null;
        trackStartRef.current = null;
        return;
      }

      if (curId === prevIdRef.current) {
        prevTrackRef.current = currentTrack;
        return;
      }

      if (prev && trackStartRef.current && prevIdRef.current) {
        const listenedMs = Date.now() - trackStartRef.current;
        const totalMs = (prev.duration || 0) * 1000;
        const eventType = totalMs > 0 && listenedMs < totalMs * 0.5
          ? 'track_skipped'
          : 'track_completed';
        trackEvent(eventType, {
          entityType: 'track',
          entityId: prevIdRef.current,
          entityName: prev.title || prev.name,
          metadata: {
            artistName: prev.artist || prev.artistName,
            albumId: prev.albumId,
            albumTitle: prev.album || prev.albumTitle || null,
            artworkUrl: prev.artworkUrl || prev.image || null,
            durationMs: listenedMs,
            totalDurationMs: totalMs,
            source: prev.source || 'unknown',
          },
        });
      }

      if (!currentTrack._fromHistory) {
        const dedupeKey = trackPlayedDedupeKey(currentTrack, queueIndex);
        const isPhantomReplay = dedupeKey && dedupeKey === _lastTrackPlayedDedupeKey;
        const meta = {
          artistName: currentTrack.artist || currentTrack.artistName,
          albumId: currentTrack.albumId,
          albumTitle: currentTrack.album || currentTrack.albumTitle || null,
          artworkUrl: currentTrack.artworkUrl || currentTrack.image || null,
          source: currentTrack.source || 'unknown',
        };
        if (currentTrack.file_path) meta.file_path = currentTrack.file_path;
        if (currentTrack.machine_id) meta.machine_id = currentTrack.machine_id;
        if (currentTrack.embedded_cover_hash) meta.embedded_cover_hash = currentTrack.embedded_cover_hash;
        if (currentTrack.folder_cover_hash) meta.folder_cover_hash = currentTrack.folder_cover_hash;
        if (currentTrack.playlistContext) meta.playlistId = currentTrack.playlistContext;
        if (!isPhantomReplay) {
          trackEvent('track_played', {
            entityType: 'track',
            entityId: curId,
            entityName: currentTrack.title || currentTrack.name,
            metadata: meta,
          });
          if (dedupeKey) _lastTrackPlayedDedupeKey = dedupeKey;
        }

        saveRecentAlbumFromTrack(currentTrack);

        if (meta.albumId && meta.albumTitle && isStorableStreamLibraryAlbum({
          entity_id: String(meta.albumId),
          source: meta.source,
          file_path: meta.file_path,
        })) {
          const albumKey = `album:${meta.albumId}`;
          if (!_streamLibrarySent.has(albumKey)) {
            _streamLibrarySent.add(albumKey);
            streamLibraryService.add({
              entity_type: 'album',
              entity_id: String(meta.albumId),
              title: meta.albumTitle,
              cover_url: meta.artworkUrl || undefined,
              artist_name: meta.artistName || undefined,
              source: 'qobuz',
            }).catch(() => {});
          }
        }

        if (meta.artistName) {
          const artistId = currentTrack.artistId || currentTrack.artist_id;
          if (artistId && isStorableStreamLibraryArtist({
            entity_id: String(artistId),
            source: meta.source,
          })) {
            const artistKey = `artist:${artistId}`;
            if (!_streamLibrarySent.has(artistKey)) {
              _streamLibrarySent.add(artistKey);
              streamLibraryService.add({
                entity_type: 'artist',
                entity_id: String(artistId),
                title: meta.artistName,
                cover_url: meta.artworkUrl || undefined,
                source: 'qobuz',
              }).catch(() => {});
            }
          }
        }
      }

      prevTrackRef.current = currentTrack;
      prevIdRef.current = curId;
      trackStartRef.current = Date.now();
    });
    return unsub;
  }, []);
}
